import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Activity as ActivityIcon, Filter, FileText, Image, Video, Wifi, CreditCard } from 'lucide-react';

const ACTION_ICONS = {
  signup: FileText,
  post_created: FileText,
  post_published: FileText,
  post_published_scheduled: FileText,
  image_generated: Image,
  video_generation_started: Video,
  channel_connected: Wifi,
  channel_disconnected: Wifi,
  credits_adjusted: CreditCard,
};

export default function ActivityPage() {
  const { t } = useTranslation();
  const { api } = useAuth();
  const [activities, setActivities] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    setLoading(true);
    const params = { limit: 50 };
    if (filter !== 'all') params.action = filter;
    api.get('/api/activity', { params })
      .then(res => {
        setActivities(res.data.activities);
        setTotal(res.data.total);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [api, filter]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          {t('activity.title')}
        </h1>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[200px] bg-transparent border-white/10" data-testid="activity-filter">
            <Filter size={14} className="mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[hsl(var(--popover))] border-white/10">
            <SelectItem value="all">{t('activity.action')}: All</SelectItem>
            <SelectItem value="post_created">{t('activity.post_created')}</SelectItem>
            <SelectItem value="post_published">{t('activity.post_published')}</SelectItem>
            <SelectItem value="image_generated">{t('activity.image_generated')}</SelectItem>
            <SelectItem value="channel_connected">{t('activity.channel_connected')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="rounded-xl border border-white/10 bg-[hsl(var(--card))]/80">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[hsl(var(--primary))]"></div>
            </div>
          ) : activities.length === 0 ? (
            <div className="text-center py-12">
              <ActivityIcon size={40} className="mx-auto mb-3 text-[hsl(var(--muted-foreground))]" />
              <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('activity.noActivity')}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-[hsl(var(--muted-foreground))]">{t('activity.action')}</TableHead>
                  <TableHead className="text-[hsl(var(--muted-foreground))]">{t('activity.details')}</TableHead>
                  <TableHead className="text-[hsl(var(--muted-foreground))] text-right">{t('activity.date')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activities.map((act, i) => {
                  const Icon = ACTION_ICONS[act.action] || ActivityIcon;
                  return (
                    <TableRow key={i} className="border-white/5 hover:bg-white/5" data-testid={`activity-row-${i}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Icon size={14} className="text-[hsl(var(--accent))]" />
                          <span className="text-sm">{t(`activity.${act.action}`, act.action)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-[hsl(var(--muted-foreground))]">
                          {act.details?.channel || act.details?.channels?.join(', ') || act.details?.prompt?.substring(0, 40) || '-'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="text-xs text-[hsl(var(--muted-foreground))]">
                          {new Date(act.created_at).toLocaleString()}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
